import Requester from '@cobliteam/api-lib/src/request/Requester';
import Environment from '@cobliteam/react-environments';
import { MastraClient } from '@mastra/client-js';
import type { AIUploadedFile } from '../types';
import { timeZoneHeader } from './timezoneHeader';

const environment = Environment.getInstance().getEnvironment();
const requester = new Requester({ host: environment.mastraApi });

export const MASTRA_BASE_URL = environment.mastraApi;

export const controller = new AbortController();

export interface MastraClientOptions {
  /**
   * Sid de uma sessão Cobli **dedicada**, enviado como `cobli-api-sid`.
   *
   * Só a criação/edição de rotina usa isto: o `cobli/ai` lê o header e grava no `requestContext` do
   * schedule, para o cron ter credencial viva quando disparar horas depois — o bearer desta chamada
   * já teria expirado. Ver `lib/automations/routineSession`.
   *
   * Vai no header, e não no corpo, de propósito: `metadata` do workflow e `inputData` do schedule
   * voltam para o cliente na listagem (`automationMappers`), e um sid ali seria legível por qualquer
   * pessoa da frota.
   */
  routineSessionId?: string | null;
}

export const mastraClient = async ({ routineSessionId }: MastraClientOptions = {}) => {
  const accessToken = await requester.getValidToken();
  return new MastraClient({
    baseUrl: MASTRA_BASE_URL,
    abortSignal: controller.signal,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...timeZoneHeader(),
      ...(routineSessionId ? { 'cobli-api-sid': routineSessionId } : {}),
    },
  });
};

export interface MastraFetchOptions extends Omit<RequestInit, 'body'> {
  body?: Record<string, any>;
  stream?: boolean;
}

/**
 * Erro de uma chamada ao ai-api com o status e o corpo preservados.
 *
 * A UI precisa distinguir falha de recusa esperada — 429 de rate limit vira mensagem de "já
 * recebemos seu pedido" em vez de erro — e a mensagem em texto puro não dava esse gancho.
 */
export class MastraFetchError extends Error {
  readonly status: number;
  readonly body: string;
  /** `error`/`code` do corpo JSON, quando o ai-api responde com um. */
  readonly code?: string;

  constructor(status: number, body: string) {
    super(`AI-API request failed (${status}): ${body}`);
    this.name = 'MastraFetchError';
    this.status = status;
    this.body = body;
    this.code = parseErrorCode(body);
  }
}

const parseErrorCode = (body: string): string | undefined => {
  try {
    const parsed = JSON.parse(body);
    const code = parsed?.error ?? parsed?.code;
    return typeof code === 'string' ? code : undefined;
  } catch {
    return undefined;
  }
};

/**
 * O ai-api sinaliza rate limit com 429; o código no corpo é checado junto porque um gateway
 * intermediário pode reescrever o status.
 */
export const isRateLimitedError = (error: unknown): boolean => error instanceof MastraFetchError && (error.status === 429 || error.code === 'rate_limited');

export const mastraFetch = async (path: string, options: MastraFetchOptions = {}): Promise<Response> => {
  const accessToken = await requester.getValidToken();
  const { body, stream: _stream, ...rest } = options;

  const response = await fetch(`${MASTRA_BASE_URL}${path}`, {
    ...rest,
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      ...timeZoneHeader(),
      ...(rest.headers as Record<string, string>),
    },
    // A per-call signal wins over the shared one: long-lived streams need to be cancellable
    // on their own (a run the user aborted) without tearing down every other AI request.
    signal: rest.signal ?? controller.signal,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new MastraFetchError(response.status, errorText);
  }

  return response;
};

export type UploadAttachmentOptions = {
  threadId?: string;
  resourceId?: string;
};

/**
 * Upload a user attachment to the AI backend, which stores it in S3 and returns the URL.
 *
 * Uses a raw `fetch` with `FormData` on purpose: the shared `Requester`/`CobliAxios` forces
 * `Content-Type: application/json`, which would strip the multipart boundary. Leaving the header
 * unset lets the browser set `multipart/form-data; boundary=…` automatically.
 */
export const uploadAttachment = async (file: File, { threadId, resourceId }: UploadAttachmentOptions = {}): Promise<AIUploadedFile> => {
  const accessToken = await requester.getValidToken();

  const form = new FormData();
  form.append('file', file);
  if (threadId) form.append('threadId', threadId);
  if (resourceId) form.append('resourceId', resourceId);

  const response = await fetch(`${MASTRA_BASE_URL}/api/v1/files/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}`, ...timeZoneHeader() },
    body: form,
    signal: controller.signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`AI file upload failed (${response.status}): ${errorText}`);
  }

  return (await response.json()) as AIUploadedFile;
};

export default mastraClient;
